import {useState, useMemo} from 'react'
import {useDialog} from '@/hooks/useDialog'
import {useParams} from 'react-router-dom'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {Card, CardContent, CardHeader, CardTitle} from '@/components/ui/card'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {useTrace} from '@/hooks/use-observability'
import {routes} from '@/lib/routes'
import {notFoundIfMissing} from '@/lib/not-found-error'
import {SpanWaterfall} from '@/components/observability/SpanWaterfall'
import {SpanDetailSheet} from '@/components/observability/SpanDetailSheet'
import type {Span} from '@/types/observability'
import {SpanStatus} from '@/types/observability'
import {Stack} from '@/components/shared/Stack'
import {InlineRow} from '@/components/shared/InlineRow'
import {MutedText} from '@/components/shared/MutedText'
import {MonoText} from '@/components/shared/MonoText'
import {PageLayout} from '@/components/shared/PageLayout'
import {CONCEPT_ICON} from '@/lib/concepts'

function collectDescendantSpanIds(spans: Span[], rootId: string): Set<string> {
    const ids = new Set<string>()
    const queue = [rootId]
    while (queue.length > 0) {
        const current = queue.shift()!
        for (const span of spans) {
            if (span.parentSpanId === current && !ids.has(span.spanId)) {
                ids.add(span.spanId)
                queue.push(span.spanId)
            }
        }
    }
    return ids
}

function statusBadgeVariant(code: number | null): BadgeVariant {
    if (code === null) return BadgeVariant.OUTLINE
    if (code >= 500) return BadgeVariant.DESTRUCTIVE
    if (code >= 400) return BadgeVariant.WARNING
    if (code >= 200 && code < 300) return BadgeVariant.SUCCESS
    return BadgeVariant.OUTLINE
}

export function RequestDetail() {
    const i18n = useI18n()
    usePageTitle(i18n.requestInspector.requestDetail.title)
    const {sessionId, spanId} = useParams<{sessionId: string; spanId: string}>()
    const decodedSessionId = sessionId ? decodeURIComponent(sessionId) : undefined

    const {data: trace, isLoading, error} = useTrace(decodedSessionId)

    const [selectedSpan, setSelectedSpan] = useState<Span | null>(null)
    const sheetDialog = useDialog()

    const handleSpanClick = (span: Span) => {
        setSelectedSpan(span)
        sheetDialog.open()
    }

    const {requestSpan, filteredSpans} = useMemo(() => {
        if (!trace || !spanId) return {requestSpan: null, filteredSpans: []}
        const req = trace.spans.find(s => s.spanId === spanId) ?? null
        if (!req) return {requestSpan: null, filteredSpans: []}
        const descendantIds = collectDescendantSpanIds(trace.spans, spanId)
        const all = [req, ...trace.spans.filter(s => descendantIds.has(s.spanId))]
        return {requestSpan: req, filteredSpans: all}
    }, [trace, spanId])

    const hasError = requestSpan ? requestSpan.status === SpanStatus.ERROR : false

    const header = requestSpan ? (
        <InlineRow align="start" className="gap-4 mb-6">
            <Stack gap="xs">
                <InlineRow gap="lg" className="mb-1">
                    <Badge variant="outline" className="font-mono text-sm">
                        {requestSpan.httpMethod ?? '—'}
                    </Badge>
                    <MonoText className="text-xl font-bold" title={requestSpan.httpUrl ?? undefined}>
                        {requestSpan.httpUrl ?? requestSpan.operationName}
                    </MonoText>
                </InlineRow>
                <InlineRow>
                    {requestSpan.httpStatusCode !== null && (
                        <Badge variant={statusBadgeVariant(requestSpan.httpStatusCode)}>
                            {requestSpan.httpStatusCode}
                        </Badge>
                    )}
                    <Badge variant={hasError ? BadgeVariant.DESTRUCTIVE : BadgeVariant.SUCCESS}>
                        {requestSpan.status}
                    </Badge>
                    {/* eslint-disable-next-line i18next/no-literal-string -- unit suffix */}
                    <MutedText>{requestSpan.durationMs}ms</MutedText>
                </InlineRow>
            </Stack>
        </InlineRow>
    ) : null

    return (
        <PageLayout
            data-testid="request-detail-page"
            variant="detail"
            icon={CONCEPT_ICON.requestInspector}
            title={requestSpan?.httpUrl ?? requestSpan?.operationName ?? i18n.requestInspector.requestDetail.title}
            loading={isLoading}
            error={error ?? (!isLoading ? notFoundIfMissing(trace, 'REQUEST') : undefined)}
            backTo={routes.admin.requestInspector.detail(sessionId ?? '')}
            description={header}
        >
            {trace && requestSpan && (
                <>
                    <Stack gap="lg">
                        <Card>
                            <CardHeader>
                                <CardTitle data-testid="card-title-waterfall">{i18n.requestInspector.waterfall.title}</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {filteredSpans.length === 0 ? (
                                    <MutedText>{i18n.requestInspector.requestDetail.noSpans}</MutedText>
                                ) : (
                                    <SpanWaterfall
                                        spans={filteredSpans}
                                        onSpanClick={handleSpanClick}
                                        selectedSpanId={selectedSpan?.spanId}
                                    />
                                )}
                            </CardContent>
                        </Card>
                    </Stack>

                    <SpanDetailSheet
                        span={selectedSpan}
                        open={sheetDialog.isOpen}
                        onOpenChange={sheetDialog.bind.onOpenChange}
                    />
                </>
            )}
        </PageLayout>
    )
}
